import { CreateEvaluationMutationVariables } from "@/graphql/generated/graphql";
import { CreateEvaluation } from "@/graphql/mutations/CreateEvaluation";
import { useGraphMutation, useToast } from "@dashboard/library";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

export function useCreateEvaluation() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { show } = useToast();

  const [mutation, { loading }] = useGraphMutation(CreateEvaluation, {
    update(cache, { data }) {
      const createEvaluation = data?.createEvaluation;

      if (createEvaluation?.__typename === "Evaluation") {
        /**
         * We add the new Evaluation to getEvaluations through the cache,
         * to avoid having to refetch the query.
         *
         * More info here: https://www.apollographql.com/docs/react/data/mutations/#refetching-queries
         */
        cache.modify({
          id: "ROOT_QUERY",
          fields: {
            getEvaluations(cached, { toReference }) {
              const ref = toReference({
                __typename: "Evaluation",
                ID: createEvaluation.ID,
              });

              return { ...cached, items: [ref] };
            },
          },
        });
      }
    },
    onCompleted(data) {
      if (data.createEvaluation.__typename === "Evaluation") {
        show({
          message: t("evaluation_published"),
          variant: "success",
        });

        /**
         * Go back to /evaluations page
         */
        navigate("../");

        return;
      }

      show({
        variant: "critical",
        message: data?.createEvaluation.message || t("error.generic.title"),
      });
    },
    onError() {
      show({
        variant: "critical",
        message: t("error.generic.title"),
      });
    },
  });

  const createEvaluation = async (input: CreateEvaluationMutationVariables) => {
    mutation({
      variables: input,
    });
  };

  return {
    createEvaluation,
    loadingCreateEvaluation: loading,
  };
}
